// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.UserAccountStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import AppGlobal from 'Izenda.AppGlobal';
import fetcher from 'fetchers/Izenda.UserAccountDataFetcher';
import systemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import roleSetupFetcher from 'fetchers/Izenda.RoleSetupFetcher';
import localizer from 'share/localization/Izenda.Localizer';
import UserAccount from './models/Izenda.UserAccount';
import UserAccountDetail from './models/Izenda.UserAccountDetail';
import MobxUtils from 'share/utils/Izenda.MobxUtils';
import uiState from 'logic/Izenda.UiState';
import constant from 'logic/data/Izenda.UserAccountConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import { invoke, isEmpty, get, isNil, split, filter, flatMap, cloneDeep, isFunction } from 'lodash/fp';

@tracker
class UserAccountStore {
  constructor(loadUsers: any, userModeType: any) {
    this.loadUsers = loadUsers;
    this.userModeType = userModeType; // This config for get avaiable user
  }

  onPrePopulateUserAccounts: any = null;

  @observable model = new UserAccount();
  @observable configUser = null;
  @observable configUserCloned = null;
  @observable alertMessage = {};
  @observable isLoadingData = false;

  @computed
  get tenantId() {
    return uiState.getTenantId();
  }

  @computed
  get isDirty() {
    return this.configUserCloned.isDirty;
  }

  /**
  * Load all user accounts
  */
  @action
  loadAllUserAccounts(): any {
    const model = this.model;
    const populateUserAccounts = action(userAccount => {
      this.isLoadingData = false;
      userAccount && this.model.populateUserAccountList(userAccount);
      return Promise.resolve(true);
    });

    model.pageInfo.buildPagingRequest();
    this.isLoadingData = true;

    const fetcherFn = isNil(this.loadUsers)
      ? fetcher.loadAllUserAccounts(this.tenantId, model.pageInfo, null, this.userModeType)
      : this.loadUsers(model.pageInfo);

    return fetcherFn.then((userAccounts) => {
      isFunction(this.onPrePopulateUserAccounts) && this.onPrePopulateUserAccounts(userAccounts);
      return populateUserAccounts(userAccounts);
    });
  }

  /**
  * Load all user accounts
  */
 getAllUserAccounts(): any {
   const model = this.model;

   model.pageInfo.buildPagingRequest();
   model.pageInfo.pageIndex = 0;
   model.pageInfo.pageSize = constant.DEFAULT_VALUE.MAX_RECORDS_FETCH;
   return fetcher.loadAllUserAccounts(this.tenantId, model.pageInfo, null, this.userModeType);
 }

  init(): void {
    uiState.setCheckDirtyFunction(() => this.isDirty);
  }

  /**
   * destroy observe
   * @method destroy
   */
  destroy(): void {
    invoke('disposeReaction', this.model);
    uiState.resetCheckDirtyFunction();
  }

  /**
   * load All User Setup
   * @return {Promise}
   */
  @action
  loadAllUserSetup() {
    const tenantId = this.tenantId;
    const model = this.model;
    const onlyBasicInfo = true;

    return Promise.join(
      systemSettingFetcher.loadAllLanguages(),
      systemSettingFetcher.loadAllDateFormat(),
      roleSetupFetcher.getRoleList(tenantId, null, onlyBasicInfo),
      action((languageList, dateFormatList, roleList) => {
        model.populateLanguageList(languageList);
        model.populateDateFormatList(dateFormatList);
        model.populateRoleList(roleList);
      })
    );
  }

  /**
   * set Select First User
   * @return {promise}
   */
  @action
  setSelectFirstUser() {
    if (!isEmpty(this.model.userAccountList)) {
      this.selectUser(this.model.userAccountList[0]);
    } else {
      this.addUser();
    }
    return Promise.resolve(true);
  }

  /**
   * Handle when page size change
   */
  @action
  onPageSizeChange(pageSize: number) {
    this.model.pageInfo.setPageSize(pageSize);
    return this.goToPage(1);
  }

  /**
   * Handle go to page
   * @param  {int} pageIndex
   */
  @action
  goToPage(pageIndex: number) {
    const pageInfo = this.model.pageInfo;
    pageInfo.setPageIndex(pageIndex);
    return this.loadAllUserAccounts();
  }

  /**
   * Search subscription
   * @param  {string} searchProperty
   * @param  {string} searchText
   */
  @action
  search(searchProperty: string, searchText: string) {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
    return this.goToPage(1);
  }

  /**
   * Change sort orders
   * @param {string} columnKey
   * @param {object} sortDir
   */
  @action
  onSortChange(columnKey: string, sortDir: any) {
    this.model.pageInfo.setColSortDirs(columnKey, sortDir);
    return this.loadAllUserAccounts();
  }

  /**
   * Update filter criteria information.
   * @param {String} searchProperty
   * @param {String} searchText
   */
  @action
  updateSearchCriteria(searchProperty: string, searchText: string): void {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
  }

    /**
   * Update column sort orders
   * @param {string} columnKey
   * @param {object} sortDir
   */
  @action
  updateColSortDirs(columnKey: string, sortDir: any) {
    this.model.pageInfo.setColSortDirs(columnKey, sortDir);
  }

  /**
   * load user list
   * @return {promise}
   */
  @action
  loadUserList() {
    const tenantId = this.tenantId;
    return systemSettingFetcher.getAllUsers(tenantId).then(respone => this.usersList.readUserListInfo(respone));
  }

  /**
   * Load partial user list for lazy loading
   * @return {promise}
   */
  @action
  getPartialUsers(pageInfo: any, isSearching: boolean, searchKey = null) {
    const userAccountList = get('model.userAccountList', this);
    const isFirstLoad = isNil(pageInfo) || isEmpty(userAccountList);
    pageInfo = pageInfo || this.lazyLoadingStore.pageInfoForFirstTime;

    const params = {
      tenantId: this.tenantId,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      criteria: [
        {
          key: constant.USER_LIST_SEARCH_KEY,
          value: searchKey
        }
      ]
    };

    return systemSettingFetcher.getPartialUsers(params).then(respone => {
      const filterText = get('filterText', this.model);
      (searchKey === filterText || isEmpty(searchKey) === isEmpty(filterText)) &&
        this.model.populateLazyUserAccountList(respone, isSearching);
      isFirstLoad && this.setSelectFirstUser();
    });
  }

  /**
   * add new user
   * @type {[type]}
   */
  @action
  addUser(): void {
    const otherConfig = AppGlobal.advancedSettings.other;
    this.model.resetAllSelected();
    this.resetAlertMessage();
    this.configUser = new UserAccountDetail();
    this.configUser.dataOffset = otherConfig.timezoneForDataOffsetValue;
    this.configUser.timestampOffset = otherConfig.timezoneForTimestampOffsetValue;
    this.cloneUserSetupDetail();
  }

  /**
   * select User
   * @param  {object} user
   */
  @action
  selectUser(user: any): void {
    this.resetAlertMessage();
    this.model.resetAllSelected();
    this.configUser = user;
    this.configUser.updateSelected(true);
    this.cloneUserSetupDetail();
  }

  /**
   * reset alert message
   */
  @action
  resetAlertMessage(): void {
    this.alertMessage = {};
  }

  /**
   * generate Password Link
   */
  @action
  generatePasswordLink() {
    const user = this.configUserCloned;
    const data = {
      id: user.id,
      username: user.userName,
      firstname: user.firstName,
      lastname: user.lastName,
      emailaddress: user.emailAddress
    };
    return fetcher.generatePasswordLink(data).then(user.populatePasswordLink.bind(user));
  }

  /**
   * set User Status
   * @param {object} userInfo
   * @param {boolean} activate
   */
  @action
  setUserStatus(userInfo: any, activate: boolean) {
    const fn = activate ? fetcher.activateUser : fetcher.deactivateUser;
    const readUserStatus = action(result => {
      if (isNil(result.messages)) {
        userInfo.populateData(result);
        this.cloneUserSetupDetail();
        return true;
      }
      return result;
    });
    return fn(userInfo)
      .bind(this)
      .then(readUserStatus);
  }

  /**
   * clone User Setup Detail
   */
  @action
  cloneUserSetupDetail(): void {
    this.configUserCloned = new UserAccountDetail(cloneDeep(MobxUtils.toJS(this.configUser)));
  }

  /**
   * save user setup detail cloned
   * @return {promise}
   */
  @action
  save() {
    const configUser = this.configUser;
    const configUserCloned = this.configUserCloned;
    const data = configUserCloned.getDataForSave(configUserCloned);
    const handleAfterSave = action(result => {
      if (isNil(result.messages)) {
        result.hasChangeLanguage = configUserCloned.hasChangeLanguage;
        if (configUserCloned.isNew) {
          const newUser = this.model.addUser(result.data);
          this.selectUser(newUser);
        } else {
          configUser.populateData(result.data);
          configUserCloned.populateData(result.data);
        }
        // populate data for my profile
        if (uiState.currentUser.id === result.data.id) {
          uiState.currentUser.populate(result.data);
        }
        configUserCloned.resetDirty();
        configUserCloned.resetRecipientValue();
        configUserCloned.resetPasswordLink();
        this.alertMessage = {
          type: 'success',
          message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
        };
      }

      return result;
    });

    Object.assign(data, {
      tenantId: this.tenantId
    });
    return fetcher
      .save(data)
      .bind(this)
      .then(handleAfterSave);
  }

  /**
   * save Password Options
   * @return {promise}
   */
  @action
  savePasswordOptions() {
    const data = this.configUserCloned.getDataForSavePasswordOptions();
    const handleAfterSave = action(success => {
      if (success) {
        this.alertMessage = {
          type: 'success',
          message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
        };
        return success;
      }

      return null;
    });
    return fetcher
      .savePasswordOptions(data)
      .bind(this)
      .then(handleAfterSave);
  }

  /**
   * delete User
   * @param  {object} user
   */
  @action
  deleteUser(user: any) {
    const userId = user.id;
    const handleDelete = action(result => {
      if (isNil(result.messages)) {
        // Remove delete user
        this.model.deleteUser(user);
        this.setSelectFirstUser();
      }
      return result;
    });
    return fetcher
      .deleteUser(userId)
      .bind(this)
      .then(handleDelete);
  }

  /**
   * find RoleId
   * @return {array}
   */
  findRoleId(oldRoles: any, newRoles: any) {
    const listRoles = [];
    const listOldRoles = oldRoles && split(',', oldRoles);
    !isEmpty(listOldRoles) &&
      listOldRoles.forEach(role => {
        !String.searchString(newRoles, role) && listRoles.push(role);
      });

    return listRoles;
  }

  /**
   * validate User Role Association
   * @return {promise}
   */
  validateUserRoleAssociation() {
    const configUserCloned = this.configUserCloned;

    if (configUserCloned.systemAdmin) {
      return Promise.resolve(true);
    }

    const oldRoles = this.configUser.rolesValue;
    const newRoles = configUserCloned.rolesValue;
    // find remove role id based on the the original role
    const removedRoleIds = this.findRoleId(oldRoles, newRoles);
    // find newly added role
    const addedRoleIds = this.findRoleId(newRoles, oldRoles);

    if (isEmpty(removedRoleIds)) {
      return Promise.resolve(true);
    }
    const data = {
      userId: configUserCloned.id,
      removedRoleIds: removedRoleIds,
      addedRoleIds: addedRoleIds
    };
    return fetcher.validateUserRoleAssociation(data).then(result => {
      if (!isNil(result.messages)) {
        !isEmpty(result.messages[0]) &&
          configUserCloned.populateAfterValidateRoleAssociation(result.messages[0].detail);
      }

      return result;
    });
  }

  /**
   * check Last System Admin
   * @return {boolean}
   */
  checkLastSystemAdmin() {
    return fetcher.checkLastSystemAdmin();
  }

  /**
   * check Last System Admin
   * @return {boolean}
   */
  @action
  validateEmails(data: any): boolean {
    const users = filter(user => {
      return isEmpty(user.emailAddress) || isNil(user.emailAddress);
    }, data);
    const message = localizer.getText('DASHBOARD_SCHEDULING_CONSTANTS_RECIPIENT_NO_EMAIL');
    const errorMesaage = String.format(message, flatMap('fullName', users));
    this.alertMessage = { type: 'error', message: errorMesaage };
  }

  /**
   * Register lazy loading store
   */
  @action
  registerLazyLoadingStore(store): void {
    this.lazyLoadingStore = store;
  }
}

export default UserAccountStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.UserAccountStore.js